import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import {
  Modal,
  Table,
  Select,
  Input,
  InputNumber,
  DatePicker,
  Button,
  Space,
  Spin,
  message,
  Row,
  Col,
  Divider,
  Empty,
} from "antd";
import {
  SearchOutlined,
  CloseOutlined,
  PlusOutlined,
  PrinterOutlined,
  FileExcelOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import CAxios from "../Utils/CAxios";
import { errorMsg, successMsg } from "../Utils/Utils";
import { saveAs } from "file-saver";
import moment from "moment";
import locale from "antd/lib/locale/fr_FR";
import printJS from "print-js";

const OPERATORS = {
  string: [
    { value: "=", label: "=" },
    { value: "!=", label: "!=" },
    { value: "contains", label: "Contient" },
    { value: "not_contains", label: "Ne contient pas" },
    { value: "starts_with", label: "Commence par" },
    { value: "ends_with", label: "Se termine par" },
    { value: "is_null", label: "Est vide" },
    { value: "is_not_null", label: "N'est pas vide" },
  ],
  number: [
    { value: "=", label: "=" },
    { value: "!=", label: "!=" },
    { value: ">", label: ">" },
    { value: ">=", label: ">=" },
    { value: "<", label: "<" },
    { value: "<=", label: "<=" },
    { value: "between", label: "Entre" },
  ],
  datetime: [
    { value: "=", label: "=" },
    { value: "!=", label: "!=" },
    { value: ">", label: "Après le" },
    { value: ">=", label: "À partir du" },
    { value: "<", label: "Avant le" },
    { value: "<=", label: "Jusqu'au" },
    { value: "between", label: "Entre" },
  ],
  date: [
    { value: "=", label: "=" },
    { value: "!=", label: "!=" },
    { value: ">", label: "Après le" },
    { value: ">=", label: "À partir du" },
    { value: "<", label: "Avant le" },
    { value: "<=", label: "Jusqu'au" },
    { value: "between", label: "Entre" },
  ],
  boolean: [
    { value: "=", label: "Est" },
  ],
};

const DEFAULT_LIMITS = [25, 50, 100, 200];

const INITIAL_FILTER = { field: "", operator: "contains", value: "", logic: "AND" };

const AdvancedSearchModal = ({ model, visible, onClose, displayFields }) => {
  const [loading, setLoading] = useState(false);
  const [schema, setSchema] = useState({ fields: [], relations: [] });
  const [filters, setFilters] = useState([{ ...INITIAL_FILTER }]);
  const [results, setResults] = useState({
    data: [],
    total: 0,
    page: 1,
    limit: 25,
    totalPages: 0,
  });
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);
  const [exportAction, setExportAction] = useState(null);
  const [exporting, setExporting] = useState(false);

  const tableRef = useRef(null);

  const reportTitle = useMemo(() => {
    const titles = {
      Facture: "Rapport des Factures",
      Client: "Rapport des Clients",
      Article: "Rapport des Articles",
      BonLivraison: "Rapport des Bons de Livraison",
      BonReception: "Rapport des Bons de Réception",
      Quote: "Rapport des Devis",
      Provider: "Rapport des Fournisseurs",
      User: "Rapport des Utilisateurs",
      Category: "Rapport des Catégories",
      Marque: "Rapport des Marques",
      Emplacement: "Rapport des Emplacements",
    };
    return titles[model] || `Rapport des ${model}s`;
  }, [model]);

  useEffect(() => {
    if (visible) {
      loadSchema();
    }
  }, [visible, model]);

  const loadSchema = async () => {
    setLoading(true);
    setSearched(false);
    setResults({ data: [], total: 0, page: 1, limit: 25, totalPages: 0 });
    setFilters([{ ...INITIAL_FILTER }]);
    try {
      const { data } = await CAxios.get(`/api/schema/${model}`);
      if (data && data.success) {
        setSchema(data.success);
      }
    } catch (err) {
      errorMsg("Erreur lors du chargement de la structure");
    }
    setLoading(false);
  };

  const allFieldOptions = useMemo(() => {
    const options = [];
    if (schema.fields) {
      schema.fields.forEach((f) => {
        options.push({
          value: f.name,
          label: f.label,
          type: f.type,
          group: "Champs",
        });
      });
    }
    if (schema.relations) {
      schema.relations.forEach((rel) => {
        if (rel.fields) {
          rel.fields.forEach((f) => {
            options.push({
              value: `${rel.name}.${f.name}`,
              label: `${rel.label} - ${f.label}`,
              type: f.type,
              group: rel.label,
            });
          });
        }
      });
    }
    return options;
  }, [schema]);

  const fieldMap = useMemo(() => {
    const map = {};
    allFieldOptions.forEach((opt) => {
      map[opt.value] = opt;
    });
    return map;
  }, [allFieldOptions]);

  const columns = useMemo(() => {
    if (displayFields && displayFields.length > 0) {
      return displayFields.map((df) => ({
        title: df.label,
        dataIndex: df.field,
        key: df.field,
        ellipsis: true,
        width: 150,
        sorter: (a, b) => {
          const va = resolveFieldValue(a, df.field);
          const vb = resolveFieldValue(b, df.field);
          if (typeof va === "number") return va - vb;
          return String(va || "").localeCompare(String(vb || ""));
        },
        render: (_, record) => renderCellValue(record, df.field, df.type),
      }));
    }

    const cols = [];
    if (schema.fields) {
      schema.fields.forEach((f) => {
        cols.push({
          title: f.label,
          dataIndex: f.name,
          key: f.name,
          ellipsis: true,
          width: 150,
          sorter: (a, b) => {
            const va = resolveFieldValue(a, f.name);
            const vb = resolveFieldValue(b, f.name);
            if (typeof va === "number") return va - vb;
            return String(va || "").localeCompare(String(vb || ""));
          },
          render: (_, record) => renderCellValue(record, f.name, f.type),
        });
      });
    }
    if (schema.relations) {
      schema.relations.forEach((rel) => {
        if (rel.fields) {
          rel.fields.forEach((f) => {
            const fullKey = `${rel.name}.${f.name}`;
            cols.push({
              title: `${rel.label} - ${f.label}`,
              dataIndex: fullKey,
              key: fullKey,
              ellipsis: true,
              width: 150,
              sorter: (a, b) => {
                const va = resolveFieldValue(a, fullKey);
                const vb = resolveFieldValue(b, fullKey);
                return String(va || "").localeCompare(String(vb || ""));
              },
              render: (_, record) => renderCellValue(record, fullKey, f.type),
            });
          });
        }
      });
    }
    return cols;
  }, [schema, displayFields]);

  const getOperatorsForField = useCallback(
    (fieldValue) => {
      const fieldDef = fieldMap[fieldValue];
      if (!fieldDef) return OPERATORS.string;
      const type = fieldDef.type;
      if (OPERATORS[type]) return OPERATORS[type];
      if (type === "float" || type === "decimal" || type === "integer") return OPERATORS.number;
      return OPERATORS.string;
    },
    [fieldMap]
  );

  const handleFieldChange = (index, fieldValue) => {
    const newFilters = [...filters];
    const ops = getOperatorsForField(fieldValue);
    newFilters[index] = {
      ...newFilters[index],
      field: fieldValue,
      operator: ops[0]?.value || "contains",
      value: "",
    };
    setFilters(newFilters);
  };

  const handleOperatorChange = (index, opValue) => {
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], operator: opValue, value: "" };
    setFilters(newFilters);
  };

  const handleValueChange = (index, val) => {
    const newFilters = [...filters];
    newFilters[index] = { ...newFilters[index], value: val };
    setFilters(newFilters);
  };

  const addFilter = () => {
    setFilters([...filters, { ...INITIAL_FILTER }]);
  };

  const removeFilter = (index) => {
    if (filters.length <= 1) {
      setFilters([{ ...INITIAL_FILTER }]);
      return;
    }
    const newFilters = filters.filter((_, i) => i !== index);
    setFilters(newFilters);
  };

  const resetFilters = () => {
    setFilters([{ ...INITIAL_FILTER }]);
    setResults({ data: [], total: 0, page: 1, limit: 25, totalPages: 0 });
    setSearched(false);
  };

  const buildPayload = useCallback(
    (page, limit, format) => {
      const validFilters = filters
        .filter((f) => {
          if (!f.field) return false;
          const ops = getOperatorsForField(f.field);
          const selectedOp = ops.find((o) => o.value === f.operator);
          if (!selectedOp) return false;
          if (f.operator === "is_null" || f.operator === "is_not_null") return true;
          if (f.operator === "between") {
            if (Array.isArray(f.value)) return f.value.length === 2;
            return false;
          }
          return f.value !== "" && f.value !== null && f.value !== undefined;
        })
        .map((f) => {
          const fieldDef = fieldMap[f.field];
          let value = f.value;
          if (fieldDef && (fieldDef.type === "datetime" || fieldDef.type === "date")) {
            if (f.operator === "between" && Array.isArray(value)) {
              value = value.map((v) => {
                if (moment.isMoment(v)) return v.format("YYYY-MM-DD HH:mm:ss");
                return v;
              });
            } else if (moment.isMoment(value)) {
              value = value.format("YYYY-MM-DD HH:mm:ss");
            }
          }
          return { field: f.field, operator: f.operator, value, logic: "AND" };
        });

      return {
        filters: validFilters,
        pagination: { page, limit },
        format: format || "json",
      };
    },
    [filters, fieldMap, getOperatorsForField]
  );

  const doSearch = useCallback(
    async (page, limit) => {
      const payload = buildPayload(page, limit, "json");
      setSearching(true);
      setSearched(true);
      try {
        const { data } = await CAxios.post(`/api/search/${model}`, payload);
        if (data && data.success) {
          setResults(data.success);
        }
      } catch (err) {
        errorMsg("Erreur lors de la recherche");
      }
      setSearching(false);
    },
    [model, buildPayload]
  );

  const handleSearch = () => {
    doSearch(1, results.limit);
  };

  const handlePageChange = (page, pageSize) => {
    setResults((prev) => ({ ...prev, page, limit: pageSize }));
    doSearch(page, pageSize);
  };

  const handleLimitChange = (value) => {
    const limit = parseInt(value, 10);
    setResults((prev) => ({ ...prev, page: 1, limit }));
    doSearch(1, limit);
  };

  const getPrintableData = useCallback(() => {
    const fields = displayFields && displayFields.length > 0
      ? displayFields
      : columns.map((col) => ({ label: col.title, field: col.dataIndex, type: col.type }));

    return results.data.map((record) => {
      const row = {};
      fields.forEach((f) => {
        row[f.label] = renderCellValue(record, f.field, f.type);
      });
      return row;
    });
  }, [results.data, displayFields, columns]);

  const handlePrint = () => {
    if (!results.data || results.data.length === 0) {
      errorMsg("Aucune donnée à imprimer");
      return;
    }

    const fields = displayFields && displayFields.length > 0
      ? displayFields
      : columns.map((col) => ({ label: col.title, field: col.dataIndex }));

    const printable = getPrintableData();
    const properties = fields.map((f) => f.label);

    printJS({
      printable,
      properties,
      type: "json",
      gridHeaderStyle: "background-color: #4472C4; color: #fff; font-weight: bold; padding: 8px; text-align: center;",
      gridStyle: "border: 1px solid #ddd; padding: 6px; text-align: center;",
      header: `<h2 style="text-align:center;margin-bottom:20px">${reportTitle}</h2>`,
      style: "table { border-collapse: collapse; width: 100%; } th { background-color: #4472C4 !important; color: white !important; }",
      scanStyles: false,
    });
  };

  const handleExportExcel = async () => {
    const payload = buildPayload(1, 50000, "excel");
    try {
      const response = await CAxios.post(`/api/search/${model}`, {
        filters: payload.filters,
        format: "excel",
      }, { responseType: 'blob' });
      const blob = new Blob([response.data], {
        type: "application/vnd.ms-excel;charset=utf-8",
      });
      saveAs(blob, `export_${model}_${moment().format("YYYY-MM-DD_HHmmss")}.xls`);
      successMsg("Export Excel réussi");
    } catch (err) {
      errorMsg("Erreur lors de l'export Excel");
    }
  };

  const fetchAllArticlesData = async (payload) => {
    const { data } = await CAxios.post(`/api/search/${model}`, {
      filters: payload.filters,
      pagination: { page: 1, limit: 50000 },
      full: true,
    });
    return data && data.success ? data.success.data || [] : [];
  };

  const buildArticlePrintRows = (articles, type) => {
    return articles.map((element) => {
      if (type === "prix") {
        return {
          référence: element.s__reference,
          désignation: element.s__secondTitle,
          prix_ttc: Number(element.l__prixVenteTTC).toFixed(3),
          marque: element.marqueTitle || "",
        };
      }
      var stock = Number(element.stock) || 0;
      var empDisplay = "-";
      if (Array.isArray(element.emplacement) && element.emplacement.length > 0) {
        empDisplay = element.emplacement
          .map((e) => e.emp + "(" + e.stock + ")")
          .join(", ");
      }
      return {
        référence: element.s__reference,
        désignation: element.s__secondTitle,
        emplacement: empDisplay,
        qte: stock,
      };
    });
  };

  const buildExcelHtml = (rows, headers) => {
    const head = headers
      .map((h) => `<th style="text-align:center">${h}</th>`)
      .join("");
    const body = rows
      .map((row) => {
        const cells = headers
          .map((h) => `<td style="text-align:center">${row[h] ?? ""}</td>`)
          .join("");
        return `<tr>${cells}</tr>`;
      })
      .join("");
    return (
      `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel">` +
      `<head><meta charset="UTF-8"></head><body>` +
      `<table border="1">${head}${body}</table>` +
      `</body></html>`
    );
  };

  const downloadArticleExcel = (rows, type) => {
    const headers =
      type === "prix"
        ? ["référence", "désignation", "prix_ttc", "marque"]
        : ["référence", "désignation", "emplacement", "qte"];
    const blob = new Blob([buildExcelHtml(rows, headers)], {
      type: "application/vnd.ms-excel;charset=utf-8",
    });
    saveAs(
      blob,
      `export_Article_${type}_${moment().format("YYYY-MM-DD_HHmmss")}.xls`
    );
    successMsg("Export Excel réussi");
  };

  const executeArticleChoice = async (type) => {
    const action = exportAction;
    setExporting(true);
    try {
      const payload = buildPayload(1, 50000, "json");
      const articles = await fetchAllArticlesData(payload);
      if (!articles.length) {
        errorMsg("Aucune donnée à exporter");
        return;
      }
      const rows = buildArticlePrintRows(articles, type);
      const properties =
        type === "prix"
          ? ["référence", "désignation", "prix_ttc", "marque"]
          : ["référence", "désignation", "emplacement", "qte"];
      const title =
        type === "prix"
          ? "Liste des prix"
          : "Inventaire";

      if (action === "excel") {
        downloadArticleExcel(rows, type);
      } else {
        printJS({
          style: "body { margin: 0; font-size: 12px; }",
          printable: rows,
          properties: properties,
          type: "json",
          header:
            `<h2 style='text-align:center;margin-bottom:10px'>${title}</h2>` +
            `<h4>${reportTitle}</h4>`,
        });
      }
    } catch (err) {
      errorMsg("Erreur lors de l'opération");
    }
    setExporting(false);
    setExportAction(null);
  };

  const handlePrintClick = () => {
    if (!results.data || results.data.length === 0) {
      errorMsg("Aucune donnée à imprimer");
      return;
    }
    if (model === "Article") {
      setExportAction("print");
      return;
    }
    handlePrint();
  };

  const handleExportExcelClick = () => {
    if (!results.data || results.data.length === 0) {
      errorMsg("Aucune donnée à exporter");
      return;
    }
    if (model === "Article") {
      setExportAction("excel");
      return;
    }
    handleExportExcel();
  };

  const renderFilterValueInput = (filter, index) => {
    const fieldDef = fieldMap[filter.field];
    const fieldType = fieldDef?.type || "string";
    const operator = filter.operator;

    if (operator === "is_null" || operator === "is_not_null") {
      return <span style={{ padding: "0 12px", color: "#999" }}>—</span>;
    }

    if (operator === "between") {
      if (fieldType === "datetime" || fieldType === "date") {
        return (
          <DatePicker.RangePicker
            showTime={fieldType === "datetime"}
            style={{ width: 280 }}
            value={
              Array.isArray(filter.value) && filter.value.length === 2
                ? [moment(filter.value[0]), moment(filter.value[1])]
                : null
            }
            onChange={(dates) => {
              handleValueChange(
                index,
                dates ? [dates[0]?.toDate(), dates[1]?.toDate()] : []
              );
            }}
            locale={locale}
          />
        );
      }
      return (
        <Space>
          <InputNumber
            style={{ width: 130 }}
            placeholder="Min"
            value={Array.isArray(filter.value) ? filter.value[0] : undefined}
            onChange={(v) => {
              const arr = Array.isArray(filter.value) ? [...filter.value] : [undefined, undefined];
              arr[0] = v;
              handleValueChange(index, arr);
            }}
          />
          <span>et</span>
          <InputNumber
            style={{ width: 130 }}
            placeholder="Max"
            value={Array.isArray(filter.value) ? filter.value[1] : undefined}
            onChange={(v) => {
              const arr = Array.isArray(filter.value) ? [...filter.value] : [undefined, undefined];
              arr[1] = v;
              handleValueChange(index, arr);
            }}
          />
        </Space>
      );
    }

    if (fieldType === "boolean") {
      return (
        <Select
          style={{ width: 140 }}
          value={filter.value === true || filter.value === "true" ? "true" : filter.value === false || filter.value === "false" ? "false" : undefined}
          onChange={(v) => handleValueChange(index, v === "true")}
          placeholder="Sélectionner"
        >
          <Select.Option value="true">Oui</Select.Option>
          <Select.Option value="false">Non</Select.Option>
        </Select>
      );
    }

    if (fieldType === "datetime" || fieldType === "date") {
      return (
        <DatePicker
          showTime={fieldType === "datetime"}
          style={{ width: 200 }}
          value={filter.value ? moment(filter.value) : null}
          onChange={(date) => handleValueChange(index, date ? date.toDate() : "")}
          locale={locale}
        />
      );
    }

    if (fieldType === "number" || fieldType === "float" || fieldType === "decimal" || fieldType === "integer") {
      return (
        <InputNumber
          style={{ width: 200 }}
          value={filter.value}
          onChange={(v) => handleValueChange(index, v)}
          placeholder="Valeur"
        />
      );
    }

    return (
      <Input
        style={{ width: 200 }}
        value={filter.value}
        onChange={(e) => handleValueChange(index, e.target.value)}
        placeholder="Valeur"
      />
    );
  };

  const renderFilterRow = (filter, index) => {
    return (
      <Row
        key={index}
        gutter={[8, 8]}
        align="middle"
        style={{
          marginBottom: 8,
          padding: 8,
          background: "#fafafa",
          borderRadius: 6,
          border: "1px solid #f0f0f0",
        }}
      >
        <Col>
          {filters.length > 1 && (
            <Button
              danger
              shape="circle"
              size="small"
              icon={<CloseOutlined />}
              onClick={() => removeFilter(index)}
            />
          )}
        </Col>
        <Col flex="auto">
          <Space wrap>
            <Select
              style={{ width: 220 }}
              value={filter.field || undefined}
              onChange={(v) => handleFieldChange(index, v)}
              placeholder="Sélectionner un champ"
              showSearch
              filterOption={(input, option) =>
                option?.label?.toLowerCase().includes(input.toLowerCase())
              }
            >
              {allFieldOptions.map((opt) => (
                <Select.Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Select.Option>
              ))}
            </Select>

            {filter.field && (
              <Select
                style={{ width: 160 }}
                value={filter.operator}
                onChange={(v) => handleOperatorChange(index, v)}
              >
                {getOperatorsForField(filter.field).map((op) => (
                  <Select.Option key={op.value} value={op.value}>
                    {op.label}
                  </Select.Option>
                ))}
              </Select>
            )}

            {filter.field && renderFilterValueInput(filter, index)}
          </Space>
        </Col>
      </Row>
    );
  };

  const paginationControls = useMemo(() => {
    const { page, limit, total, totalPages } = results;
    return (
      <Row
        justify="end"
        align="middle"
        gutter={[16, 8]}
        style={{ marginTop: 12 }}
      >
        <Col>
          <span style={{ color: "#666" }}>
            {total > 0
              ? `${(page - 1) * limit + 1}-${Math.min(page * limit, total)} / ${total}`
              : "Aucun résultat"}
          </span>
        </Col>
        <Col>
          <Select
            value={String(limit)}
            onChange={handleLimitChange}
            style={{ width: 100 }}
          >
            {DEFAULT_LIMITS.map((l) => (
              <Select.Option key={l} value={String(l)}>
                {l} / page
              </Select.Option>
            ))}
          </Select>
        </Col>
        <Col>
          <Button
            size="small"
            disabled={page <= 1}
            onClick={() => handlePageChange(page - 1, limit)}
          >
            Précédent
          </Button>
          <span style={{ margin: "0 12px", fontWeight: 500 }}>
            Page {page} / {totalPages}
          </span>
          <Button
            size="small"
            disabled={page >= totalPages}
            onClick={() => handlePageChange(page + 1, limit)}
          >
            Suivant
          </Button>
        </Col>
      </Row>
    );
  }, [results]);

  return (
    <>
      <Modal
        className="advanced-search-modal"
        title={
          <span>
            <SearchOutlined style={{ marginRight: 8 }} />
            Recherche avancée - {model}
          </span>
        }
        visible={visible}
        onCancel={onClose}
        width="90vw"
        style={{ top: 20, maxWidth: 1400 }}
        footer={null}
        bodyStyle={{ padding: 24, maxHeight: "85vh", overflow: "auto" }}
        destroyOnClose
      >
        {loading ? (
          <div style={{ textAlign: "center", padding: 80 }}>
            <Spin size="large" tip="Chargement de la structure..." />
          </div>
        ) : (
          <>
            <div className="print-hidden">
              <Divider orientation="left" style={{ fontSize: 14, fontWeight: 600 }}>
                Filtres
              </Divider>

              {filters.map((filter, index) => renderFilterRow(filter, index))}

              <Space style={{ marginBottom: 16 }}>
                <Button
                  type="dashed"
                  icon={<PlusOutlined />}
                  onClick={addFilter}
                >
                  Ajouter une condition
                </Button>
              </Space>

              <Space style={{ marginBottom: 16 }}>
                <Button
                  type="primary"
                  icon={<SearchOutlined />}
                  onClick={handleSearch}
                  loading={searching}
                  size="large"
                >
                  Rechercher
                </Button>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={resetFilters}
                  size="large"
                >
                  Réinitialiser
                </Button>
              </Space>

              <Divider />
            </div>

            <div className="print-hidden" style={{ marginBottom: 12 }}>
              <Space>
                <Button
                  icon={<PrinterOutlined />}
                  onClick={handlePrintClick}
                  disabled={!searched || results.data.length === 0}
                >
                  Imprimer
                </Button>
                <Button
                  icon={<FileExcelOutlined />}
                  onClick={handleExportExcelClick}
                  disabled={!searched || results.data.length === 0}
                >
                  Exporter en Excel
                </Button>
                {searched && (
                  <span style={{ color: "#666", fontSize: 13 }}>
                    {results.total} résultat(s) trouvé(s)
                  </span>
                )}
              </Space>
            </div>

            <div ref={tableRef}>
              <Table
                columns={columns}
                dataSource={results.data}
                rowKey={(record, idx) => record.id || idx}
                loading={searching}
                scroll={{ x: "max-content", y: 400 }}
                size="small"
                bordered
                pagination={false}
                locale={{ emptyText: <Empty description="Aucun résultat" /> }}
              />
            </div>

            <div className="print-hidden">
              {searched && results.total > 0 && paginationControls}
            </div>
          </>
        )}
      </Modal>
      {model === "Article" && exportAction && (
        <Modal
          title="Choix d'export"
          visible={!!exportAction}
          onCancel={() => {
            if (!exporting) setExportAction(null);
          }}
          footer={null}
        >
          <Space>
            <Button
              type="primary"
              loading={exporting}
              style={{ marginRight: "1vh" }}
              onClick={() => executeArticleChoice("prix")}
            >
              Prix
            </Button>
            <Button
              icon={<FileExcelOutlined />}
              loading={exporting}
              onClick={() => executeArticleChoice("inventaire")}
            >
              Inventaire
            </Button>
          </Space>
        </Modal>
      )}
    </>
  );
};

function resolveFieldValue(record, fieldPath) {
  const parts = fieldPath.split(".");
  let value = record;
  for (const part of parts) {
    if (value == null || value === undefined) return "";
    value = value[part];
  }
  return value;
}

function renderCellValue(record, fieldPath, type) {
  const value = resolveFieldValue(record, fieldPath);
  if (value === null || value === undefined) return "";
  if (type === "boolean" || type === "bool") {
    return value ? "Oui" : "Non";
  }
  return String(value);
}

export default AdvancedSearchModal;
